function clean(value) {
  return String(value ?? "").trim();
}

function projectKey(project) {
  return clean(project?.slug || project?.id || project?._id);
}

function businessGoals(project) {
  const values = project?.onboarding?.businessGoals;
  return Array.isArray(values) ? values.map((value) => clean(value)).filter(Boolean) : [];
}

function pendingFact(facts, code) {
  const current = facts?.[code];
  return current?.required === true && current?.complete !== true;
}

function serviceFirst(project) {
  const goals = businessGoals(project);
  return goals.includes("offerServices")
    || (["services", "gym"].includes(clean(project?.businessProfile)) && !goals.includes("sellProducts"));
}

function operationalTask({ fact, base, itemPath }) {
  if (!fact || fact.required !== true || fact.complete === true) return null;

  const targets = fact?.targets && typeof fact.targets === "object" ? fact.targets : {};
  const evidence = fact?.evidence && typeof fact.evidence === "object" ? fact.evidence : {};
  const pending = ["customers", "reservations", "gastronomy", "system"].find(
    (target) => targets[target] === true && evidence[target] !== true,
  );

  if (pending === "customers") {
    return {
      id: "operational-customers",
      label: "Empezar a gestionar clientes",
      description: "Registrá o gestioná el primer cliente real del negocio.",
      path: `${base}/clientes`,
      priority: 55,
    };
  }
  if (pending === "reservations") {
    return {
      id: "operational-reservations",
      label: "Preparar reservas o agenda",
      description: "Configurá disponibilidad y empezá el circuito de turnos o reservas.",
      path: `${base}/agenda`,
      priority: 55,
    };
  }
  if (pending === "gastronomy") {
    return {
      id: "operational-gastronomy",
      label: "Preparar la operación gastronómica",
      description: "Revisá los ítems de la carta y dejá una opción real lista para operar.",
      path: itemPath,
      priority: 55,
    };
  }
  if (pending === "system") {
    return {
      id: "operational-system",
      label: "Registrar la primera operación",
      description: "Empezá Gestión del negocio con un movimiento real.",
      path: `${base}/sistema/movimientos?nuevo=1`,
      priority: 55,
    };
  }

  return {
    id: "operational-management",
    label: "Revisar configuración operativa",
    description: "Entrá a Gestión del negocio y completá la preparación pendiente.",
    path: `${base}/sistema`,
    priority: 55,
  };
}

export function buildProjectActionTasks({ project, readiness, modules = {} } = {}) {
  const key = projectKey(project);
  if (!key || !readiness || typeof readiness !== "object") return [];

  const facts = readiness?.facts && typeof readiness.facts === "object" ? readiness.facts : {};
  const base = `/dashboard/proyectos/${key}`;
  const itemPath = modules?.system ? `${base}/sistema/items` : `${base}/productos`;
  const firstService = serviceFirst(project);
  const tasks = [];

  const add = (condition, task) => {
    if (condition) tasks.push(task);
  };

  add(pendingFact(facts, "identity"), {
    id: "identity",
    label: "Completar datos del negocio",
    description: "Revisá la identidad básica del proyecto antes de seguir.",
    path: `${base}/configuracion/general`,
    priority: 10,
  });

  add(pendingFact(facts, "pageStarted"), {
    id: "page-started",
    label: "Empezar la página",
    description: "Dale forma a la portada y a los datos principales del negocio.",
    path: `${base}/pagina`,
    priority: 20,
  });

  add(pendingFact(facts, "contact"), {
    id: "contact",
    label: "Completar datos de contacto",
    description: "Sumá teléfono, WhatsApp, email o dirección para que puedan encontrarte.",
    path: `${base}/pagina?mision=datos`,
    priority: 30,
  });

  add(pendingFact(facts, "firstItem"), {
    id: "first-item",
    label: firstService ? "Crear el primer servicio" : "Crear el primer producto",
    description: firstService
      ? "Cargá un servicio real para mostrarlo, agendarlo o gestionarlo."
      : "Cargá un producto real con la información mínima para empezar.",
    path: `${itemPath}?crear=1&tipo=${firstService ? "service" : "product"}`,
    priority: 35,
  });

  add(pendingFact(facts, "publicContent") && !pendingFact(facts, "pageStarted"), {
    id: "public-content",
    label: "Preparar contenido público",
    description: "Agregá contenido propio para que la página no salga vacía.",
    path: `${base}/pagina`,
    priority: 40,
  });

  const operational = operationalTask({ fact: facts.operationalManagement, base, itemPath });
  if (operational) tasks.push(operational);

  add(pendingFact(facts, "paymentMethod"), {
    id: "payment-method",
    label: "Configurar cobros",
    description: modules?.ecommerce
      ? "Conectá Mercado Pago para habilitar el cobro online que necesita este negocio."
      : "Revisá el acceso a cobros online para completar esta configuración.",
    path: modules?.ecommerce ? `${base}/configuracion/ecommerce` : `${base}/configuracion/suscripcion`,
    priority: 60,
  });

  const domainFact = facts.customDomain;
  add(domainFact?.configured === true && domainFact?.complete !== true, {
    id: "custom-domain",
    label: domainFact?.failedCount > 0 ? "Resolver el dominio" : "Terminar de conectar el dominio",
    description: domainFact?.failedCount > 0
      ? "La validación del dominio necesita una revisión de DNS o HTTPS."
      : "La conexión ya empezó, pero todavía falta completar DNS o HTTPS.",
    path: `${base}/configuracion/general`,
    priority: 65,
  });

  add(pendingFact(facts, "publication"), {
    id: "publication",
    label: "Publicar la página",
    description: "Revisá la versión pública y dejala disponible para compartir con clientes.",
    path: `${base}/pagina?mision=publicar`,
    priority: 70,
  });

  return tasks
    .filter((task) => task && task.path)
    .sort((a, b) => Number(a.priority || 999) - Number(b.priority || 999));
}
